import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable, Subject, fromEvent, timer } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { 
  AgileDeviceMessage, 
  ClientData, 
  OCRResult, 
  NFCResult, 
  FingerprintResult 
} from './agile-device.types';
import { formatRutWithoutDots } from '@utils/rut.utils';

@Injectable({
  providedIn: 'root'
})
export class AgileDeviceService {
  private readonly WS_URL = 'wss://localhost:3013';
  private readonly CONNECTION_TIMEOUT = 2000;
  private readonly OCR_MAX_RETRIES = 5;
  private readonly NFC_MAX_RETRIES = 5;
  private readonly FINGERPRINT_MAX_RETRIES = 3;

  private socket: WebSocket | null = null;
  private readonly destroy$ = new Subject<void>();
  
  // Signals para el estado del servicio
  private readonly _isConnected = signal(false);
  private readonly _isLoading = signal(false);
  
  // Subjects para los diferentes tipos de respuesta
  private readonly messageSubject = new Subject<AgileDeviceMessage>();
  private readonly connectionSubject = new BehaviorSubject<boolean>(false);
  
  // Contadores de reintentos
  private ocrRetryCount = 0;
  private nfcRetryCount = 0;
  private fingerprintRetryCount = 0;

  // Getters públicos
  get isConnected() {
    return this._isConnected.asReadonly();
  }

  get isLoading() {
    return this._isLoading.asReadonly();
  }

  get connectionStatus$(): Observable<boolean> {
    return this.connectionSubject.asObservable();
  }

  /**
   * Establece conexión con el dispositivo Agile
   */
  connect(): Promise<boolean> {
    return new Promise((resolve) => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        resolve(true);
        return;
      }

      this.socket = new WebSocket(this.WS_URL);
      
      const connectionTimer = timer(this.CONNECTION_TIMEOUT).subscribe(() => {
        if (!this._isConnected()) {
          this.disconnect();
          resolve(false);
        }
      });

      this.socket.onopen = () => {
        connectionTimer.unsubscribe();
        this._isConnected.set(true);
        this.connectionSubject.next(true);
        this.setupMessageHandling();
        resolve(true);
      };

      this.socket.onerror = () => {
        connectionTimer.unsubscribe();
        this.disconnect();
        resolve(false);
      };

      this.socket.onclose = () => {
        this.disconnect();
      };
    });
  }

  /**
   * Desconecta del dispositivo
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    this._isConnected.set(false);
    this._isLoading.set(false);
    this.connectionSubject.next(false);
    this.resetRetryCounters();
  }

  /**
   * Inicia lectura OCR del documento
   */
  readOCR(): Observable<OCRResult> {
    return new Observable(observer => {
      if (!this.ensureConnection()) {
        observer.error('Dispositivo no conectado');
        return;
      }

      this.ocrRetryCount = 0;
      this._isLoading.set(true);
      
      const performOCR = () => {
        this.sendMessage({
          code: 'DOCUMENT',
          type: 'CAPTURE',
          subtype: 'OCR',
          createdAt: new Date().toISOString()
        });
      };

      const subscription = this.messageSubject
        .pipe(
          filter(msg => msg.code === 'DOCUMENT' && msg.subtype === 'OCR'),
          takeUntil(this.destroy$)
        )
        .subscribe(message => {
          this._isLoading.set(false);
          
          if (message.status?.code === 0) {
            const rawRut = message.data?.digitalIdentity?.identityDocuments?.[0]?.personalNumber || '';
            const clientData: ClientData = {
              rut: formatRutWithoutDots(rawRut),
              numero_documento: message.data?.digitalIdentity?.identityDocuments?.[0]?.number || ''
            };
            
            observer.next({ success: true, data: clientData });
            observer.complete();
            subscription.unsubscribe();
          } else {
            this.ocrRetryCount++;
            console.log(`[AGILE DEVICE] OCR intento ${this.ocrRetryCount} de ${this.OCR_MAX_RETRIES} falló`);
            
            if (this.ocrRetryCount >= this.OCR_MAX_RETRIES) {
              console.log('[AGILE DEVICE] OCR máximo de reintentos alcanzado, enviando error');
              observer.next({ success: false, error: 'Documento en mal estado' });
              observer.complete();
              subscription.unsubscribe();
            } else {
              // Reintento automático
              console.log(`[AGILE DEVICE] OCR reintentando en 100ms...`);
              timer(100).subscribe(() => performOCR());
            }
          }
        });

      performOCR();

      return () => {
        subscription.unsubscribe();
        this._isLoading.set(false);
      };
    });
  }

  /**
   * Inicia lectura NFC del documento
   */
  readNFC(clientData: ClientData): Observable<NFCResult> {
    return new Observable(observer => {
      if (!this.ensureConnection()) {
        observer.error('Dispositivo no conectado');
        return;
      }

      this.nfcRetryCount = 0;
      this._isLoading.set(true);

      const performNFC = () => {
        this.sendMessage({
          code: 'DOCUMENT',
          type: 'CAPTURE',
          subtype: 'NFC',
          createdAt: new Date().toISOString(),
          client: {},
          data: {
            digitalIdentity: {
              personalData: {
                givenNames: '',
                surnames: '',
                dob: '1900-01-01T00:00:00',
                gender: 0
              },
              identityDocuments: [{
                type: 'ID',
                countryCode: 'CHL',
                number: clientData.numero_documento,
                personalNumber: clientData.rut,
                issueDate: '',
                issuingAuthority: '',
                dob: '',
                expiryDate: ''
              }],
              authenticationFactors: []
            },
            config: {
              includeFacialImage: false,
              includeSignatureImage: false
            }
          }
        });
      };

      const subscription = this.messageSubject
        .pipe(
          filter(msg => msg.code === 'DOCUMENT' && msg.subtype === 'NFC'),
          takeUntil(this.destroy$)
        )
        .subscribe(message => {
          this._isLoading.set(false);
          
          if (message.status?.code === 0) {
            observer.next({ success: true });
            observer.complete();
            subscription.unsubscribe();
          } else {
            this.nfcRetryCount++;
            console.log(`[AGILE DEVICE] NFC intento ${this.nfcRetryCount} de ${this.NFC_MAX_RETRIES} falló`);
            
            if (this.nfcRetryCount >= this.NFC_MAX_RETRIES) {
              console.log('[AGILE DEVICE] NFC máximo de reintentos alcanzado, enviando error');
              observer.next({ success: false, error: 'Error de lectura de chip' });
              observer.complete();
              subscription.unsubscribe();
            } else {
              // Reintento automático con delay
              console.log(`[AGILE DEVICE] NFC reintentando en 1500ms...`);
              timer(1500).subscribe(() => performNFC());
            }
          }
        });

      // Delay inicial antes de la primera lectura NFC
      timer(1500).subscribe(() => performNFC());

      return () => {
        subscription.unsubscribe();
        this._isLoading.set(false);
      };
    });
  }

  /**
   * Inicia lectura de huella dactilar
   */
  readFingerprint(): Observable<FingerprintResult> {
    return new Observable(observer => {
      if (!this.ensureConnection()) {
        observer.error('Dispositivo no conectado');
        return;
      }

      this.fingerprintRetryCount = 0;
      this._isLoading.set(true);

      const performFingerprint = () => {
        this.sendMessage({
          code: 'PERSON',
          type: 'VERIFY',
          subtype: 'DOCUMENT',
          createdAt: new Date().toISOString(),
          client: {},
          data: {
            config: {
              threshold: 50,
              maxCapture: 1,
              minQuality: 50
            }
          }
        });
      };

      const subscription = this.messageSubject
        .pipe(
          filter(msg => msg.code === 'PERSON'),
          takeUntil(this.destroy$)
        )
        .subscribe(message => {
          this._isLoading.set(false);
          
          if (message.data?.authenticationResult?.authenticated) {
            observer.next({ success: true, data: message.data });
            observer.complete();
            subscription.unsubscribe();
          } else {
            this.fingerprintRetryCount++;
            console.log(`[AGILE DEVICE] Fingerprint intento ${this.fingerprintRetryCount} de ${this.FINGERPRINT_MAX_RETRIES} falló`);
            
            if (this.fingerprintRetryCount >= this.FINGERPRINT_MAX_RETRIES) {
              console.log('[AGILE DEVICE] Fingerprint máximo de reintentos alcanzado, enviando error');
              observer.next({ success: false, error: 'No ha sido posible verificar su identidad' });
              observer.complete();
              subscription.unsubscribe();
            } else {
              // Reintento automático
              console.log(`[AGILE DEVICE] Fingerprint reintentando en 1000ms...`);
              timer(1000).subscribe(() => performFingerprint());
            }
          }
        });

      performFingerprint();

      return () => {
        subscription.unsubscribe();
        this._isLoading.set(false);
      };
    });
  }

  /**
   * Limpia recursos al destruir el servicio
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.disconnect();
  }

  private setupMessageHandling(): void {
    if (!this.socket) return;

    fromEvent<MessageEvent>(this.socket, 'message')
      .pipe(
        map(event => JSON.parse(event.data) as AgileDeviceMessage),
        takeUntil(this.destroy$)
      )
      .subscribe(message => {
        console.log('[AGILE DEVICE] Mensaje recibido:', message);
        this.messageSubject.next(message);
      });

    fromEvent(this.socket, 'error')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        console.error('[AGILE DEVICE] Error en la conexión');
        this.disconnect();
      });

    fromEvent(this.socket, 'close')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        console.log('[AGILE DEVICE] Conexión cerrada');
        this.disconnect();
      });
  }

  private sendMessage(message: any): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    }
  }

  private ensureConnection(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  private resetRetryCounters(): void {
    this.ocrRetryCount = 0;
    this.nfcRetryCount = 0;
    this.fingerprintRetryCount = 0;
  }
}